import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const VehicleUtilizationHeatmap = () => {
  const [selectedMetric, setSelectedMetric] = useState('utilization');
  const [timeframe, setTimeframe] = useState('week');

  const vehicles = [
    { id: 'FL-001', name: 'Truck 001', type: 'Heavy Duty' },
    { id: 'FL-002', name: 'Truck 002', type: 'Medium Duty' },
    { id: 'FL-003', name: 'Truck 003', type: 'Light Duty' },
    { id: 'FL-004', name: 'Truck 004', type: 'Heavy Duty' },
    { id: 'FL-005', name: 'Truck 005', type: 'Medium Duty' },
    { id: 'FL-006', name: 'Truck 006', type: 'Light Duty' }
  ];

  const timeSlots = [
    '06:00', '08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00'
  ];

  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Mock utilization data (0-100%)
  const utilizationData = {
    'FL-001': {
      'Mon': [85, 92, 88, 95, 90, 78, 65, 45],
      'Tue': [88, 95, 92, 98, 85, 72, 58, 40],
      'Wed': [82, 89, 85, 92, 88, 75, 62, 48],
      'Thu': [90, 96, 94, 99, 92, 80, 68, 52],
      'Fri': [87, 93, 89, 96, 91, 77, 64, 46],
      'Sat': [45, 52, 48, 55, 50, 42, 35, 25],
      'Sun': [25, 30, 28, 35, 32, 28, 22, 15]
    },
    'FL-002': {
      'Mon': [78, 85, 82, 88, 84, 71, 58, 38],
      'Tue': [82, 89, 86, 92, 88, 75, 62, 42],
      'Wed': [75, 82, 79, 85, 81, 68, 55, 35],
      'Thu': [85, 92, 89, 95, 91, 78, 65, 45],
      'Fri': [80, 87, 84, 90, 86, 73, 60, 40],
      'Sat': [38, 45, 42, 48, 44, 36, 29, 19],
      'Sun': [20, 25, 23, 28, 26, 22, 18, 12]
    },
    'FL-003': {
      'Mon': [92, 98, 95, 99, 96, 88, 75, 55],
      'Tue': [95, 99, 98, 100, 97, 90, 78, 58],
      'Wed': [89, 95, 92, 98, 94, 85, 72, 52],
      'Thu': [96, 100, 99, 100, 98, 92, 80, 60],
      'Fri': [93, 99, 96, 99, 95, 87, 74, 54],
      'Sat': [52, 58, 55, 62, 58, 50, 42, 32],
      'Sun': [32, 38, 35, 42, 38, 32, 26, 18]
    },
    'FL-004': {
      'Mon': [68, 75, 72, 78, 74, 61, 48, 28],
      'Tue': [72, 79, 76, 82, 78, 65, 52, 32],
      'Wed': [65, 72, 69, 75, 71, 58, 45, 25],
      'Thu': [75, 82, 79, 85, 81, 68, 55, 35],
      'Fri': [70, 77, 74, 80, 76, 63, 50, 30],
      'Sat': [28, 35, 32, 38, 34, 26, 19, 9],
      'Sun': [10, 15, 13, 18, 16, 12, 8, 2]
    },
    'FL-005': {
      'Mon': [88, 94, 91, 97, 93, 85, 72, 52],
      'Tue': [91, 97, 94, 99, 96, 88, 75, 55],
      'Wed': [85, 91, 88, 94, 90, 82, 69, 49],
      'Thu': [94, 99, 97, 100, 98, 90, 78, 58],
      'Fri': [89, 95, 92, 98, 94, 86, 73, 53],
      'Sat': [49, 55, 52, 58, 54, 46, 38, 28],
      'Sun': [28, 34, 31, 38, 34, 28, 22, 14]
    },
    'FL-006': {
      'Mon': [76, 82, 79, 85, 81, 73, 60, 40],
      'Tue': [79, 85, 82, 88, 84, 76, 63, 43],
      'Wed': [73, 79, 76, 82, 78, 70, 57, 37],
      'Thu': [82, 88, 85, 91, 87, 79, 66, 46],
      'Fri': [77, 83, 80, 86, 82, 74, 61, 41],
      'Sat': [37, 43, 40, 46, 42, 34, 27, 17],
      'Sun': [17, 23, 20, 26, 22, 18, 14, 8]
    }
  };

  const getUtilizationColor = (value) => {
    if (value >= 90) return 'bg-success';
    if (value >= 70) return 'bg-warning';
    if (value >= 50) return 'bg-accent';
    if (value >= 30) return 'bg-primary';
    return 'bg-muted-foreground';
  };

  const getUtilizationIntensity = (value) => {
    const intensity = Math.min(value / 100, 1);
    return {
      opacity: 0.3 + (intensity * 0.7)
    };
  };

  const getAverageUtilization = (vehicleId) => {
    const data = utilizationData?.[vehicleId];
    let total = 0;
    let count = 0;
    
    Object.values(data)?.forEach(dayData => {
      dayData?.forEach(value => {
        total += value;
        count++;
      });
    });
    
    return Math.round(total / count);
  };

  const getPeakHours = (vehicleId) => {
    const data = utilizationData?.[vehicleId];
    const hourlyAverages = timeSlots?.map((_, hourIndex) => {
      let total = 0;
      let count = 0;
      
      Object.values(data)?.forEach(dayData => {
        total += dayData?.[hourIndex];
        count++;
      });
      
      return { hour: timeSlots?.[hourIndex], avg: total / count };
    });
    
    const peak = hourlyAverages?.reduce((max, current) => 
      current?.avg > max?.avg ? current : max
    );
    
    return peak?.hour;
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground">Vehicle Utilization Heatmap</h3>
        
        <div className="flex items-center space-x-3">
          {/* Metric Selector */}
          <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
            <button
              onClick={() => setSelectedMetric('utilization')}
              className={`px-3 py-1 text-xs font-medium rounded-md nav-transition ${
                selectedMetric === 'utilization' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Utilization
            </button>
            <button
              onClick={() => setSelectedMetric('efficiency')}
              className={`px-3 py-1 text-xs font-medium rounded-md nav-transition ${
                selectedMetric === 'efficiency' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Efficiency
            </button>
          </div>

          {/* Timeframe Selector */}
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e?.target?.value)}
            className="px-3 py-1 text-xs bg-background border border-border rounded-lg text-foreground"
          >
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
          </select>
        </div>
      </div>
      {/* Heatmap */}
      <div className="p-4">
        <div className="overflow-x-auto">
          <div className="min-w-full">
            {/* Time Headers */}
            <div className="flex mb-2">
              <div className="w-24 flex-shrink-0"></div>
              {days?.map(day => (
                <div key={day} className="flex-1 min-w-0">
                  <div className="text-center text-sm font-medium text-muted-foreground mb-2">
                    {day}
                  </div>
                  <div className="grid grid-cols-8 gap-1">
                    {timeSlots?.map(time => (
                      <div key={time} className="text-xs text-muted-foreground text-center">
                        {time?.slice(0, 2)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Vehicle Rows */}
            <div className="space-y-2">
              {vehicles?.map(vehicle => (
                <div key={vehicle?.id} className="flex items-center">
                  {/* Vehicle Info */}
                  <div className="w-24 flex-shrink-0 pr-3">
                    <div className="text-sm font-medium text-foreground">{vehicle?.id}</div>
                    <div className="text-xs text-muted-foreground">{vehicle?.type}</div>
                  </div>

                  {/* Heatmap Cells */}
                  {days?.map(day => (
                    <div key={day} className="flex-1 min-w-0">
                      <div className="grid grid-cols-8 gap-1">
                        {utilizationData?.[vehicle?.id]?.[day]?.map((value, timeIndex) => (
                          <div
                            key={timeIndex}
                            className={`h-6 rounded ${getUtilizationColor(value)} cursor-pointer hover:opacity-80 nav-transition`}
                            style={getUtilizationIntensity(value)}
                            title={`${vehicle?.id} - ${day} ${timeSlots?.[timeIndex]}: ${value}% utilization`}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-foreground">Utilization:</span>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-muted-foreground rounded opacity-30"></div>
              <span className="text-xs text-muted-foreground">0-30%</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-primary rounded opacity-50"></div>
              <span className="text-xs text-muted-foreground">30-50%</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-accent rounded opacity-70"></div>
              <span className="text-xs text-muted-foreground">50-70%</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-warning rounded opacity-80"></div>
              <span className="text-xs text-muted-foreground">70-90%</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-success rounded"></div>
              <span className="text-xs text-muted-foreground">90-100%</span>
            </div>
          </div>
          
          <button className="flex items-center space-x-1 px-3 py-1 text-xs font-medium text-muted-foreground hover:text-foreground bg-background border border-border rounded-lg nav-transition">
            <Icon name="Download" size={14} />
            <span>Export</span>
          </button>
        </div>
      </div>
      {/* Summary Stats */}
      <div className="p-4 bg-muted border-t border-border">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-lg font-bold text-foreground">
              {Math.round(vehicles?.reduce((sum, v) => sum + getAverageUtilization(v?.id), 0) / vehicles?.length)}%
            </div>
            <div className="text-xs text-muted-foreground">Fleet Average</div>
          </div>
          
          <div className="text-center">
            <div className="text-lg font-bold text-success">
              {vehicles?.filter(v => getAverageUtilization(v?.id) >= 80)?.length}
            </div>
            <div className="text-xs text-muted-foreground">High Utilization</div>
          </div>
          
          <div className="text-center">
            <div className="text-lg font-bold text-warning">
              {vehicles?.filter(v => getAverageUtilization(v?.id) < 50)?.length}
            </div>
            <div className="text-xs text-muted-foreground">Underutilized</div>
          </div>
          
          <div className="text-center">
            <div className="text-lg font-bold text-foreground">10-14</div>
            <div className="text-xs text-muted-foreground">Peak Hours</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleUtilizationHeatmap;